import React from 'react';
import { ExclamationTriangleIcon, ClockIcon } from '@heroicons/react/24/outline';

// Helper function to format timestamp
const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'Never';
    const date = new Date(timestamp);
    const now = new Date();
    const diffMinutes = Math.floor((now - date) / (1000 * 60));
    
    if (diffMinutes < 1) return 'Just now';
    if (diffMinutes < 60) return `${diffMinutes} minute${diffMinutes === 1 ? '' : 's'} ago`;
    
    const diffHours = Math.floor(diffMinutes / 60);
    if (diffHours < 24) return `${diffHours} hour${diffHours === 1 ? '' : 's'} ago`;
    
    const diffDays = Math.floor(diffHours / 24);
    return `${diffDays} day${diffDays === 1 ? '' : 's'} ago`;
};

// Helper function to get status badge styling
const getStatusStyling = (status) => {
    const statusLower = status?.toLowerCase() || '';
    
    if (statusLower.includes('out') || statusLower.includes('inactive')) {
        return 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300 border-red-300 dark:border-red-700';
    }
    if (statusLower.includes('doubtful')) {
        return 'bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-300 border-orange-300 dark:border-orange-700';
    }
    if (statusLower.includes('questionable') || statusLower.includes('day-to-day')) {
        return 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300 border-yellow-300 dark:border-yellow-700';
    }
    if (statusLower.includes('probable')) {
        return 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 border-green-300 dark:border-green-700';
    }
    
    // Default
    return 'bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-300 border-gray-300 dark:border-gray-600';
};

// Individual injury item component
const InjuryItem = ({ injury, sport }) => {
    // Sport-specific data extraction
    const getInjuryData = () => {
        if (sport === 'nfl') {
            return {
                name: injury.Name || 'Unknown Player',
                position: injury.Position || 'N/A',
                status: injury.Status || 'Unknown',
                details: injury.DeclaredInactive ? 'Declared Inactive' : null
            };
        } else {
            // NBA and MLB
            return {
                name: injury.Name || 'Unknown Player',
                position: injury.Position || 'N/A',
                status: injury.InjuryStatus || 'Unknown',
                details: injury.InjuryNotes || null
            };
        }
    };
    
    const injuryData = getInjuryData();
    const statusStyling = getStatusStyling(injuryData.status);
    
    return (
        <div className="flex items-start justify-between p-3 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg hover:shadow-sm transition-shadow">
            <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                    <div className="font-semibold text-gray-900 dark:text-white">{injuryData.name}</div>
                    {injuryData.position && (
                        <span className="text-xs text-gray-500 dark:text-gray-400 px-2 py-0.5 bg-gray-100 dark:bg-gray-700 rounded">
                            {injuryData.position}
                        </span>
                    )}
                </div>
                {injuryData.details && (
                    <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">{injuryData.details}</div>
                )}
            </div>
            <div className={`ml-3 px-2 py-1 text-xs font-medium rounded border ${statusStyling} whitespace-nowrap`}>
                {injuryData.status}
            </div>
        </div>
    );
};

// Main InjuriesView component
const InjuriesView = ({ homeTeam, awayTeam, homeInjuries, awayInjuries, sport, lastUpdated = null }) => {
    const hasInjuries = (homeInjuries && homeInjuries.length > 0) || (awayInjuries && awayInjuries.length > 0);
    
    return (
        <div className="relative bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg shadow-md p-6">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-2">
                    <ExclamationTriangleIcon className="h-6 w-6 text-gray-700 dark:text-gray-300" />
                    <h3 className="text-xl font-bold text-gray-900 dark:text-white">Injury Report</h3>
                </div>
                {lastUpdated && (
                    <div className="flex items-center gap-1 text-sm text-gray-500 dark:text-gray-400">
                        <ClockIcon className="h-4 w-4" />
                        <span>{formatTimestamp(lastUpdated)}</span>
                    </div>
                )}
            </div>
            
            {!hasInjuries ? (
                <div className="text-center py-8">
                    <p className="text-gray-500 dark:text-gray-400">No injury data available at this time</p>
                </div>
            ) : (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Away Team Column */}
                    <div>
                        <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                            {awayTeam}
                            <span className="text-sm font-normal text-gray-500 dark:text-gray-400">
                                ({awayInjuries?.length || 0})
                            </span>
                        </h4>
                        {awayInjuries && awayInjuries.length > 0 ? (
                            <div className="space-y-2 max-h-[420px] overflow-y-auto pr-2 scrollbar-thin scrollbar-thumb-gray-300 dark:scrollbar-thumb-gray-600 scrollbar-track-transparent">
                                {awayInjuries.map((injury, idx) => (
                                    <InjuryItem key={idx} injury={injury} sport={sport} />
                                ))}
                            </div>
                        ) : (
                            <p className="text-sm text-gray-500 dark:text-gray-400 italic">No reported injuries</p>
                        )}
                    </div>
                    
                    {/* Home Team Column */}
                    <div>
                        <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                            {homeTeam}
                            <span className="text-sm font-normal text-gray-500 dark:text-gray-400">
                                ({homeInjuries?.length || 0})
                            </span>
                        </h4>
                        {homeInjuries && homeInjuries.length > 0 ? (
                            <div className="space-y-2 max-h-[420px] overflow-y-auto pr-2 scrollbar-thin scrollbar-thumb-gray-300 dark:scrollbar-thumb-gray-600 scrollbar-track-transparent">
                                {homeInjuries.map((injury, idx) => (
                                    <InjuryItem key={idx} injury={injury} sport={sport} />
                                ))}
                            </div>
                        ) : (
                            <p className="text-sm text-gray-500 dark:text-gray-400 italic">No reported injuries</p>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default InjuriesView;